// 완료한 기록만 집계한다. 기록되지 않은 선택 결과나 인과관계는 추정하지 않는다.
import { GROUPS, OPEN_THRESHOLD } from './rules.js';

const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
const number = (v) => (Number.isFinite(v) ? Math.round(v * 10) / 10 : null);
const fmt = (v) => (v == null ? '-' : `${v >= 0 ? '+' : ''}${number(v).toFixed(1).replace(/\.0$/, '')}`);
const yearDiff = (year, group) => Number.isFinite(year.stats?.[group]) && Number.isFinite(year.before?.[group])
  ? year.stats[group] - year.before[group] : null;

function recordedEvents(run) {
  const logs = (run.log ?? []).filter((entry) => entry.kind === 'event');
  const records = Array.isArray(run.eventHistory) ? run.eventHistory : [];
  const merged = records.map((entry) => ({ ...logs.find((log) => log.eventId === entry.eventId && log.age === entry.age), ...entry }));
  for (const entry of logs) {
    if (!records.some((record) => record.eventId && record.eventId === entry.eventId && record.age === entry.age)) merged.push(entry);
  }
  return merged.map((entry, index) => {
    const parsed = /^(\S+) ([+-]\d+(?:\.\d+)?)/.exec(entry.result || '');
    const field = entry.field ?? entry.targetField ?? parsed?.[1] ?? null;
    const delta = Number.isFinite(entry.delta) ? entry.delta
      : Number.isFinite(entry.effects?.[field]) ? entry.effects[field] : parsed ? Number(parsed[2]) : null;
    const outcome = entry.outcome ?? (typeof entry.failed === 'boolean' ? entry.failed ? 'failure' : 'success' : null);
    const textParts = (entry.text ?? '').split(' → ');
    return { ...entry, field, group: field, delta, outcome,
      key: entry.eventId ? `${entry.eventId}@${entry.age}` : `legacy-${entry.age}-${index}`,
      title: entry.title ?? entry.eventTitle ?? textParts[0] ?? '기록된 사건',
      choiceLabel: entry.choiceLabel ?? entry.label ?? (textParts.slice(1).join(' → ') || null),
      effects: entry.effects ?? (field && delta != null ? { [field]: delta } : {}),
      text: entry.text ?? `${entry.title ?? entry.eventId ?? '사건'}${entry.choiceLabel ? ` → ${entry.choiceLabel}` : ''}`,
    };
  }).sort((a, b) => a.age - b.age);
}

function eventPaths(events) {
  const byKey = new Map(events.map((entry) => [entry.key, entry]));
  const buckets = new Map();
  for (const entry of events) {
    let root = entry;
    const visited = new Set([entry.key]);
    while (root.parentKey && byKey.has(root.parentKey) && !visited.has(root.parentKey)) {
      visited.add(root.parentKey); root = byKey.get(root.parentKey);
    }
    if (!buckets.has(root.key)) buckets.set(root.key, { key: root.key, title: root.title, events: [] });
    buckets.get(root.key).events.push(entry);
  }
  return [...buckets.values()].sort((a, b) => a.events[0].age - b.events[0].age);
}

export function summarize(run) {
  const h = Array.isArray(run.history) ? run.history : [];
  const events = recordedEvents(run);
  let opened = 0, actedOpen = 0;
  const afterAct = [], afterSkip = [];
  for (const year of h) {
    if (year.age < 15) continue;
    for (const group of GROUPS) {
      if (!Number.isFinite(year.opens?.[group]) || year.opens[group] < OPEN_THRESHOLD) continue;
      opened += 1;
      const delta = yearDiff(year, group);
      if ((year.acted ?? []).includes(group)) { actedOpen += 1; if (delta != null) afterAct.push(delta); }
      else if (delta != null) afterSkip.push(delta);
    }
  }

  const fields = GROUPS.map((group) => {
    const selected = events.filter((entry) => entry.field === group);
    const start = number(run.saju?.stats?.[group] ?? h[0]?.before?.[group]);
    const end = number(run.stats?.[group] ?? h.at(-1)?.stats?.[group]);
    return { group, start, end, change: start != null && end != null ? number(end - start) : null,
      actedYears: h.filter((year) => year.acted?.includes(group)).length,
      openedYears: h.filter((year) => year.age >= 15 && year.opens?.[group] >= OPEN_THRESHOLD).length,
      focusYears: new Set((run.log ?? []).filter((log) => log.kind === 'focus' && log.field === group).map((log) => log.age)).size,
      attempts: selected.filter((entry) => entry.outcome === 'success' || entry.outcome === 'failure').length,
      successes: selected.filter((entry) => entry.outcome === 'success').length,
      failures: selected.filter((entry) => entry.outcome === 'failure').length,
      declines: selected.filter((entry) => entry.outcome === 'skipped' || entry.choiceKind === 'decline').length,
      unknown: selected.filter((entry) => !entry.outcome).length,
    };
  });
  const picks = events.filter((entry) => entry.field && entry.delta != null)
    .map((entry) => ({ ...entry, open: (h.find((year) => year.age === entry.age)?.opens?.[entry.field] ?? 0) >= OPEN_THRESHOLD,
      failed: entry.outcome === 'failure' || entry.failed }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).slice(0, 3).sort((a, b) => a.age - b.age);

  let low = null;
  const windowSize = 8;
  const adult = h.filter((year) => year.age >= 15);
  for (let i = 0; i + windowSize <= adult.length; i += 1) {
    const window = adult.slice(i, i + windowSize);
    const count = window.reduce((total, year) => total + GROUPS.filter((group) => year.opens?.[group] >= OPEN_THRESHOLD).length, 0);
    if (!low || count < low.cnt) low = { window, cnt: count };
  }
  const lowInfo = low ? { from: low.window[0].age, to: low.window.at(-1).age, cnt: low.cnt,
    acts: low.window.filter((year) => year.acted?.length).length,
    change: low.window.reduce((total, year) => total + GROUPS.reduce((sum, group) => sum + (yearDiff(year, group) ?? 0), 0), 0) / GROUPS.length,
  } : null;

  const relationships = (run.people ?? []).map((person) => {
    const memories = [...(person.memories ?? []), ...(person.notes ?? [])];
    for (const entry of events) {
      const participants = entry.participantIds ?? (entry.participantId ? [entry.participantId] : entry.personId ? [entry.personId] : []);
      if (participants.includes(person.id)) memories.push({ age: entry.age, text: entry.text, outcome: entry.outcome, eventKey: entry.key });
    }
    const unique = new Map(memories.map((memory) => [`${memory.age}:${memory.text ?? memory.label ?? memory.eventKey}`, memory]));
    return { ...person, memories: [...unique.values()].sort((a, b) => a.age - b.age) };
  });
  const timeYears = h.filter((year) => year.timePlan && typeof year.timePlan === 'object' && year.timePlan.capacity > 0);
  const focusYears = new Set((run.log ?? []).filter((entry) => entry.kind === 'focus').map((entry) => entry.age)).size;
  const eventYears = new Set(events.filter((entry) => ['success', 'failure'].includes(entry.outcome)).map((entry) => entry.age)).size;
  const declineYears = new Set(events.filter((entry) => entry.outcome === 'skipped' || entry.choiceKind === 'decline').map((entry) => entry.age)).size;
  const units = (key) => timeYears.reduce((sum, year) => sum + (Number.isFinite(year.timePlan[key]) ? year.timePlan[key] : 0), 0);

  return { endAge: Math.max(0, (run.age ?? 1) - 1), opened, actedOpen,
    avgAct: mean(afterAct), avgSkip: mean(afterSkip), fmt, picks, lowInfo, fields, events,
    paths: eventPaths(events), relationships,
    jobs: (run.log ?? []).filter((entry) => entry.kind === 'job').sort((a, b) => a.age - b.age),
    lifeEvents: (run.log ?? []).filter((entry) => entry.kind === 'lifeEvent').sort((a, b) => a.age - b.age),
    time: { focusYears, eventYears, declineYears, recordedYears: timeYears.length, years: timeYears,
      focusUnits: units('focusUnits'), eventUnits: units('eventUnits'), relationshipUnits: units('relationshipUnits'), remaining: units('remaining') },
    unknownOutcomes: events.filter((entry) => !entry.outcome).length,
    maxOpen: Math.max(15, ...h.filter((year) => year.age >= 15).flatMap((year) => GROUPS.map((group) => year.opens?.[group] ?? 0))),
  };
}

export function compareLives(current, previous) {
  if (!previous) return null;
  const currentSummary = summarize(current), previousSummary = summarize(previous);
  const decisions = (run, summary) => {
    const map = new Map();
    const append = (age, text) => { const items = map.get(age) ?? []; items.push(text); map.set(age, items); };
    const personName = (id) => (run.people ?? []).find((person) => person.id === id)?.name ?? '기록된 인물';
    for (const entry of summary.events) {
      append(entry.age, `사건: ${entry.title} → ${entry.choiceLabel ?? (entry.choiceIdx != null ? `${entry.choiceIdx + 1}번 선택` : '선택 문구 미기록')}${entry.participantId ? ` · ${personName(entry.participantId)}와 함께` : ''}`);
    }
    for (const entry of (run.log ?? []).filter((item) => item.kind === 'focus')) {
      const items = map.get(entry.age) ?? [];
      items.push(`집중: ${entry.field ?? entry.text}`); map.set(entry.age, items);
    }
    for (const year of run.history ?? []) {
      const plan = year.timePlan;
      if (plan?.capacity > 0) append(year.age, `시간: 집중 ${plan.focusUnits ?? 0}칸 · 사건 ${plan.eventUnits ?? 0}칸 · 관계 ${plan.relationshipUnits ?? 0}칸 · 남김 ${plan.remaining ?? 0}칸`);
    }
    for (const entry of run.decisionTrace ?? []) {
      if (entry.kind === 'relationship') {
        const choice = { help: '함께하기', limited: '범위 줄이기', decline: '거절하기', promise: '다음 해 약속하기' }[entry.choiceKind] ?? '선택 내용 미기록';
        append(entry.age, `관계: ${personName(entry.personId)} · ${choice}`);
      } else if (entry.kind === 'job') {
        const job = run.referenceData?.jobs?.find((item) => item.id === entry.jobId);
        append(entry.age, `직업: ${entry.jobId == null ? '이번 제안 거절' : job?.name ?? entry.label ?? '기록된 직업 선택'}`);
      } else if (entry.kind === 'jobChange') {
        const job = run.referenceData?.jobs?.find((item) => item.id === entry.jobId);
        append(entry.age, `전직: ${entry.jobId == null ? '제안 거절' : job?.name ?? entry.label ?? '기록된 전직 선택'}`);
      } else if (entry.kind === 'branch') {
        append(entry.age, `직업 갈래: ${entry.label ?? (entry.branchIdx != null ? `${entry.branchIdx + 1}번째 갈래` : '기록된 갈래 선택')}`);
      } else if (entry.kind === 'meet') {
        append(entry.age, `만남: ${entry.personId ? personName(entry.personId) : entry.pick == null ? '가까이하지 않기' : `${entry.pick + 1}번째 인물 선택`}${entry.dropId ? ` · ${personName(entry.dropId)}와 거리 두기` : ''}`);
      } else if (entry.kind === 'drift') append(entry.age, `관계: ${personName(entry.personId)}와 거리 두기`);
    }
    for (const items of map.values()) items.sort();
    return map;
  };
  const now = decisions(current, currentSummary), before = decisions(previous, previousSummary);
  const ages = [...new Set([...now.keys(), ...before.keys()])].sort((a, b) => a - b);
  const firstAge = ages.find((age) => JSON.stringify(now.get(age) ?? []) !== JSON.stringify(before.get(age) ?? []));
  return { current: currentSummary, previous: previousSummary,
    firstDifference: firstAge == null ? null : { age: firstAge, current: now.get(firstAge) ?? [], previous: before.get(firstAge) ?? [] },
    sameRules: current.engineRulesVersion != null && current.engineRulesVersion === previous.engineRulesVersion
      && [current, previous].every((run) => (run.history ?? []).every((year) => (year.engineRulesVersion ?? year.balanceVersion) === run.engineRulesVersion))
      && !!current.referenceData?.fingerprint && current.referenceData.fingerprint === previous.referenceData?.fingerprint,
  };
}
